import { createContext, useContext, useEffect, useState, type ReactNode, type FC, useRef } from "react";
import { useTranslation } from "react-i18next";
import { onAuthStateChanged, type User } from "firebase/auth";
import { auth, db } from "../firebase/firebaseConfig";
import { doc, setDoc, serverTimestamp, addDoc, collection, getDoc } from "firebase/firestore";
import { useSessionTimeout } from "../hooks/useSessionTimeout";
import SessionTimeoutWarning from "../components/auth/SessionTimeoutWarning";
import GlobalLoader from "../components/common/GlobalLoader";
import auditLogger from "../utils/auditLogger";
import { navigateTo } from "../utils/navigation";
import { t } from "i18next";
import { Capacitor } from "@capacitor/core";
import { hydrateNativeSessionToFirebaseJs } from "../services/nativeAuthHydrationService";
 

type AuthContextValue = {
  user: User | null;
  loading: boolean;
};

const AuthContext = createContext<AuthContextValue>({
  user: null, loading: true });

function AuthProviderInner({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [showWarning, setShowWarning] = useState(false);
  const provisioningRef = useRef<Set<string>>(new Set());
  const pendingNullAuthRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hydrationCompleteRef = useRef(false);
  const authResolvedRef = useRef(false);

  const { i18n } = useTranslation();

  const { timeLeft } = useSessionTimeout({
    inactivityTimeout: 1440, // 1 día de inactividad
    maxSessionDuration: 24, // 24 horas máximo
    onWarning: () => setShowWarning(true),
    onExpire: () => {
      setShowWarning(false);
      const isAdminApp = typeof window !== 'undefined' && window.location.pathname.includes('admin.html');
      navigateTo(isAdminApp ? "/admin.html#/admin/signin" : "/signin");
    },
  });

  useEffect(() => {
    // El plugin nativo maneja la autenticación directamente
    // No necesitamos getRedirectResult - todo se procesa en los formularios

    (async () => {
      try {
        const platform = Capacitor.getPlatform();
        if (platform === 'ios') {
          await hydrateNativeSessionToFirebaseJs();
        }
      } catch {
      } finally {
        hydrationCompleteRef.current = true;
      }
    })();

    const safetyTimeoutMs = (() => {
      try {
        const platform = Capacitor.getPlatform();
        const isNative = platform === 'android' || platform === 'ios';
        return isNative ? 15000 : 10000;
      } catch {
        return 10000;
      }
    })();

    // En native, dar tiempo a Firebase para restaurar la sesión persistida
    // antes de resolver loading=false (evita flash de login page).
    const minNativeWaitMs = (() => {
      try {
        const platform = Capacitor.getPlatform();
        const isNative = platform === 'android' || platform === 'ios';
        return isNative ? 3000 : 0;
      } catch {
        return 0;
      }
    })();
    const minWaitUntil = Date.now() + minNativeWaitMs;

    const hardStopAt = Date.now() + safetyTimeoutMs;
    let safetyTimeout: ReturnType<typeof setTimeout>;
    const scheduleSafetyCheck = () => {
      safetyTimeout = setTimeout(() => {
        try {
          const platform = Capacitor.getPlatform();
          if (platform === 'ios' && !hydrationCompleteRef.current && Date.now() < hardStopAt) {
            scheduleSafetyCheck();
            return;
          }
          // En native, esperar al menos minNativeWaitMs para que Firebase
          // restaure la sesión persistida antes de dar loading=false.
          const isNative = platform === 'android' || platform === 'ios';
          if (isNative && Date.now() < minWaitUntil && Date.now() < hardStopAt) {
            scheduleSafetyCheck();
            return;
          }

          // En web, evitar poner loading=false antes de que onAuthStateChanged
          // responda al menos una vez (evita flash de /signin en refresh).
          if (!isNative && !authResolvedRef.current && Date.now() < hardStopAt) {
            scheduleSafetyCheck();
            return;
          }
        } catch {
        }
        setLoading(false);
      }, 500);
    };
    scheduleSafetyCheck();

    const unsub = onAuthStateChanged(auth, (u) => {
      authResolvedRef.current = true;
      try {
        const platform = Capacitor.getPlatform();
        const isNative = platform === 'android' || platform === 'ios';

        let forceSignOut = false;
        try {
          forceSignOut = localStorage.getItem('force_signout') === '1';
        } catch {
        }

        if (isNative && !u && forceSignOut) {
          try {
            localStorage.removeItem('force_signout');
          } catch {
          }
          if (pendingNullAuthRef.current) {
            clearTimeout(pendingNullAuthRef.current);
            pendingNullAuthRef.current = null;
          }
          setUser(null);
          setLoading(false);
          return;
        }

        if (isNative && !u && !hydrationCompleteRef.current) {
          return;
        }
        if (isNative && !u) {
          if (pendingNullAuthRef.current) {
            clearTimeout(pendingNullAuthRef.current);
          }
          pendingNullAuthRef.current = setTimeout(() => {
            setUser(null);
            setLoading(false);
            pendingNullAuthRef.current = null;
          }, 4000);
          return;
        }
      } catch {
      }

      if (pendingNullAuthRef.current) {
        clearTimeout(pendingNullAuthRef.current);
        pendingNullAuthRef.current = null;
      }

      setUser(u);
      setLoading(false);

      try {
        if (u) {
          localStorage.setItem('analytics_user_id', u.uid);
        } else {
          localStorage.removeItem('analytics_user_id');
        }
      } catch {
      }

      clearTimeout(safetyTimeout);
      
      const provision = async () => {
        try {
          if (u) {
            // Prevent duplicate provisioning
            if (provisioningRef.current.has(u.uid)) {
              return;
            }
            provisioningRef.current.add(u.uid);
            
            const userRef = doc(db, "users", u.uid);
            
            // Do provisioning in background without blocking UI
            let snap = null;
            try {
              snap = await getDoc(userRef);
              // Actualizar fecha de última actividad y último login del usuario
              await setDoc(userRef, { 
                lastActiveAt: serverTimestamp(),
                lastLoginAt: serverTimestamp(),
                lastLogin: serverTimestamp(),
              }, { merge: true });
            } catch (err) {
              provisioningRef.current.delete(u.uid);
              return;
            }
            
            const d = snap.exists() ? (snap.data() as any) : null;

            // Apply saved language (first login / cross-device)
            try {
              const lang = (d?.language as string | undefined) || undefined;
              const base = (lang || '').split('-')[0];
              if (base === 'es' || base === 'en' || base === 'pt') {
                await i18n.changeLanguage(base);
              }
            } catch {
              // ignore language errors
            }
            const fullName = (d?.fullName || [d?.firstName, d?.lastName].filter(Boolean).join(" ") || u.displayName || t('common.user')).trim();
            let memberId: string | null = (snap.exists() ? (snap.data() as any)?.memberId : null) || null;
            
            // Create member in background if needed
            if (!memberId) {
              try {
                const memberRef = await addDoc(collection(db, "members"), {
                  ownerUserId: u.uid,
                  userId: u.uid,
                  name: fullName,
                  displayName: fullName,
                  colorHex: "#3B82F6",
                  createdAt: serverTimestamp(),
                  familyId: null,
                });
                memberId = memberRef.id;
                await setDoc(userRef, { memberId }, { merge: true });
              } catch (memberErr) {
                // Will retry later
              }
            }
            
            // Mark provisioning complete
            provisioningRef.current.delete(u.uid);
            
            // Session tracking
            const isAdminApp = typeof window !== 'undefined' && window.location.pathname.includes('admin.html');
            const startKey = isAdminApp ? "admin_session_start_time" : "session_start_time";
            const activityKey = isAdminApp ? "admin_last_activity_time" : "last_activity_time";
            
            if (!localStorage.getItem(startKey)) {
              localStorage.setItem(startKey, Date.now().toString());
              localStorage.setItem(activityKey, Date.now().toString());
              // Log de login
              auditLogger.logUserLogin(u.uid, u.email || undefined);
            }
          } else {
            // user signed out
            const prevUserId = localStorage.getItem("prev_user_id");
            if (prevUserId) {
              auditLogger.logUserLogout(prevUserId);
              localStorage.removeItem("prev_user_id");
            }
            const isAdminApp = typeof window !== 'undefined' && window.location.pathname.includes('admin.html');
            const startKey = isAdminApp ? "admin_session_start_time" : "session_start_time";
            const activityKey = isAdminApp ? "admin_last_activity_time" : "last_activity_time";
            localStorage.removeItem(startKey);
            localStorage.removeItem(activityKey);
          }
          
          // Guardar userId para logout
          if (u) {
            localStorage.setItem("prev_user_id", u.uid);
          }
        } catch (e) {
          setLoading(false);
        }
      };
      provision();
    });

    return () => {
      clearTimeout(safetyTimeout);
      if (pendingNullAuthRef.current) {
        clearTimeout(pendingNullAuthRef.current);
        pendingNullAuthRef.current = null;
      }
      unsub();
    };
  }, []);

  useEffect(() => {
    if (loading) return;
    if (user) return;
    // Don't navigate here - let RequireAuth handle it to avoid race conditions
    // during session restoration
    const path = window.location.pathname || '';
    if (path.includes('/signin') || path.includes('/signup')) return;
    // Only navigate if hydration is complete AND we're not on initial load
    if (!hydrationCompleteRef.current) return;
    // Commented out to let RequireAuth handle navigation with proper delays
    // navigateTo('/signin');
  }, [user, loading]);

  const handleExtendSession = () => {
    setShowWarning(false);
    // El hook resetará automáticamente el timer con la actividad
  };

  return (
    <AuthContext.Provider value={{ user, loading }}>
      {/* Mostrar loader solo si está cargando Y no estamos en páginas públicas */}
      {children}
      {loading &&
      !window.location.pathname.includes("/signin") &&
      !window.location.pathname.includes("/signup") ? (
        <GlobalLoader message={t('auth.verificandoautenticacion')} />
      ) : null}
      {showWarning && timeLeft !== null && user && (
        <SessionTimeoutWarning minutesLeft={timeLeft} onExtend={handleExtendSession} />
      )}
    </AuthContext.Provider>
  );
}

export const AuthProvider: FC<{ children: ReactNode }> = ({ children }) => {
  return <AuthProviderInner>{children}</AuthProviderInner>;
};

export function useAuth(): AuthContextValue {
  return useContext(AuthContext);
}
